import { useState, useEffect } from 'react';
import axios from 'axios';

const lessons = [
  {
    id: 1,
    title: 'Greetings in the Classroom',
    sections: [
      {
        title: 'Grammar: Verb Be and Subject Pronouns',
        exercises: [
          {
            type: 'fill-in-table',
            question: 'Complete column 1 with words from the list. Then write the contractions in column 2.',
            words: ['she', 'are', 'they', 'is', 'I', 'are', 'is'],
            table: [
              { full: 'I am', contraction: '' },
              { full: 'you ___', contraction: '' },
              { full: 'he ___', contraction: '' },
              { full: '___ is', contraction: '' },
              { full: 'it ___', contraction: '' },
              { full: 'we ___', contraction: '' },
              { full: '___ are', contraction: '' },
            ],
            answers: [
              'I', 'I’m', 'are', 'You’re', 'is', 'He’s', 'she', 'She’s', 
              'is', 'It’s', 'are', 'We’re', 'they', 'They’re'
            ],
          },
          {
            type: 'fill-in-blanks',
            question: "Complete the sentences with 'm, 's, or 're.",
            sentences: [
              "Hello! you___ in my class.",
              "It___ a timetable.",
              "I___ Ganesh.",
              "We___ in room 317.",
              "He___ catching a bus.",
              "They___ doctors.",
            ],
            answers: ["'re", "'s", "'m", "'re", "'s", "'re"],
          },
          {
            type: 'fill-in-conversation',
            question: "Complete the conversations with subject pronouns and 'm, 's, or 're.",
            conversations: [
              { prompt: "What's your phone number?", answer: "It's ___ 314 892." },
              { prompt: 'How are you?', answer: '___ fine, thanks.' },
              { prompt: 'Anita and Binoy are students.', answer: '___ at university in Bombay.' },
              { prompt: 'Is Anita in your salsa class?', answer: 'No, ___ in my English class!' },
              { prompt: "Hi! I'm Monika.", answer: '___ my partner.' },
              { prompt: 'This is Mohit.', answer: '___ your teacher.' },
              { prompt: 'Are you and Omar in Class 2?', answer: 'No, ___ in Class 3.' },
            ],
            answers: ['it’s', 'I’m', 'They’re', 'she’s', 'This is', 'He’s', 'he’s'],
          },
        ],
      },
      {
        title: 'Pronunciation: Vowel Sounds, Word Stress',
        exercises: [
          {
            type: 'match-sounds',
            question: 'Match the words with their vowel sounds.',
            words: [
              { word: 'meet', sound: '' },
              { word: 'fine', sound: '' },
              { word: 'six', sound: '' },
              { word: 'man', sound: '' },
              { word: 'ten', sound: '' },
              { word: 'eight', sound: '' },
              { word: 'tree', sound: '' },
              { word: 'well', sound: '' },
              { word: 'nice', sound: '' },
              { word: 'in', sound: '' },
              { word: 'thanks', sound: '' },
              { word: 'day', sound: '' },
            ],
            sounds: ['ɪ', 'æ', 'aɪ', 'ɛ', 'iː', 'eɪ'],
            answers: [
              'iː', 'aɪ', 'ɪ', 'æ', 'ɛ', 'eɪ', 
              'iː', 'ɛ', 'aɪ', 'ɪ', 'æ', 'eɪ'
            ],
          },
          {
            type: 'stress-words',
            question: 'Underline the stressed syllable in these words.',
            words: [
              'sandwich', 'tennis', 'eighteen', 'thirteen', 
              'basketball', 'goodbye', 'email', 'internet', 
              'computer', 'hotel'
            ],
            answers: [
              'sandwich', 'tennis', 'eighteen', 'thirteen', 
              'basketball', 'goodbye', 'email', 'internet', 
              'computer', 'hotel'
            ],
          },
        ],
      },
      {
        title: 'Vocabulary: Days of the Week, Numbers 0-20',
        exercises: [
          {
            type: 'unscramble',
            question: 'Put the letters in order to make days of the week. Start with a CAPITAL LETTER.',
            words: [
              'arsaydut', 'nyauds', 'hrdytusa', 'odnyma', 
              'dfaryi', 'dsyeeawnd', 'euasdty'
            ],
            answers: ['Saturday', 'Sunday', 'Thursday', 'Monday', 'Friday', 'Wednesday', 'Tuesday'],
          },
          {
            type: 'series',
            question: 'Continue the series.',
            series: [
              'five, six, seven, eight, nine, ten, ___',
              'six, eight, ten, ___',
              'sixteen, seventeen, ___',
              'five, seven, nine, ___',
            ],
            answers: [
              'eleven', 'twelve, fourteen, sixteen', 'eighteen, nineteen, twenty', 'eleven, thirteen, fifteen'
            ],
          },
          {
            type: 'spelling-dialogue',
            question: 'Complete the words in the dialogues with correct spellings.',
            dialogues: [
              "Hello. = _'m Priya. Y_ _'re in my English cl_ss.",
              "Oh! N_c_ to m_ _t y_ _, Priya.",
              "N_c_ to m__t y__, too.",
              "Well, s_ _ y_ _ on M_nd_y, Priya.",
              "No, the cl_ _s is on T_ _sd_y, not M_nd_y!",
              "By_.",
              "G__dby_.",
            ],
            answers: [
              "I’m, You’re, class",
              "Nice, meet, you",
              "Nice, meet, you",
              "see, you, Monday",
              "class, Tuesday, Monday",
              "Bye",
              "Goodbye"
            ],
          },
        ],
      },
    ],
  },
];

const Unit1 = () => {
  const [currentLesson, setCurrentLesson] = useState(0);
  const [currentSection, setCurrentSection] = useState(0);
  const [currentExercise, setCurrentExercise] = useState(0);
  const [userAnswers, setUserAnswers] = useState({});
  const [score, setScore] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [results, setResults] = useState(null);

  const handleAnswerSubmit = async (exercise, answers) => {
    const response = await axios.post('http://localhost:5000/api/submit', {
      lessonId: lessons[currentLesson].id,
      sectionId: currentSection,
      exerciseId: currentExercise,
      answers,
    });
    const { isCorrect, correctAnswers } = response.data;
    setUserAnswers((prev) => ({
      ...prev,
      [`${currentLesson}-${currentSection}-${currentExercise}`]: { answers, isCorrect },
    }));
    if (isCorrect) setScore((prev) => prev + 1);

    // Move to next exercise, section, or lesson
    const currentSectionExercises = lessons[currentLesson].sections[currentSection].exercises;
    if (currentExercise < currentSectionExercises.length - 1) {
      setCurrentExercise(currentExercise + 1);
    } else if (currentSection < lessons[currentLesson].sections.length - 1) {
      setCurrentSection(currentSection + 1);
      setCurrentExercise(0);
    } else if (currentLesson < lessons.length - 1) {
      setCurrentLesson(currentLesson + 1);
      setCurrentSection(0);
      setCurrentExercise(0);
    } else {
      // Fetch final results
      const resultResponse = await axios.get('http://localhost:5000/api/results');
      setResults(resultResponse.data);
      setIsCompleted(true);
    }
  };

  const renderExercise = (exercise, sectionIndex, exerciseIndex) => {
    switch (exercise.type) {
      case 'fill-in-table':
        return (
          <div>
            <p>{exercise.question}</p>
            <p>Words: {exercise.words.join(', ')}</p>
            <table className="table-auto w-full mb-4">
              <thead>
                <tr>
                  <th>Full Form</th>
                  <th>Contraction</th>
                </tr>
              </thead>
              <tbody>
                {exercise.table.map((row, index) => (
                  <tr key={index}>
                    <td>
                      <input
                        type="text"
                        className="border p-2 w-full"
                        value={userAnswers[`${currentLesson}-${sectionIndex}-${exerciseIndex}`]?.answers[index * 2] || ''}
                        onChange={(e) => {
                          const newAnswers = userAnswers[`${currentLesson}-${sectionIndex}-${exerciseIndex}`]?.answers || Array(exercise.answers.length).fill('');
                          newAnswers[index * 2] = e.target.value;
                          setUserAnswers({
                            ...userAnswers,
                            [`${currentLesson}-${sectionIndex}-${exerciseIndex}`]: { answers: newAnswers },
                          });
                        }}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        className="border p-2 w-full"
                        value={userAnswers[`${currentLesson}-${sectionIndex}-${exerciseIndex}`]?.answers[index * 2 + 1] || ''}
                        onChange={(e) => {
                          const newAnswers = userAnswers[`${currentLesson}-${sectionIndex}-${exerciseIndex}`]?.answers || Array(exercise.answers.length).fill('');
                          newAnswers[index * 2 + 1] = e.target.value;
                          setUserAnswers({
                            ...userAnswers,
                            [`${currentLesson}-${sectionIndex}-${exerciseIndex}`]: { answers: newAnswers },
                          });
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button
              className="bg-blue-500 text-white px-4 py-2 rounded"
              onClick={() => handleAnswerSubmit(exercise, userAnswers[`${currentLesson}-${sectionIndex}-${exerciseIndex}`]?.answers || [])}
            >
              Submit
            </button>
          </div>
        );
      case 'fill-in-blanks':
      case 'fill-in-conversation':
        return (
          <div>
            <p>{exercise.question}</p>
            {(exercise.sentences || exercise.conversations.map(c => c.answer)).map((item, index) => (
              <div key={index} className="mb-2">
                {exercise.type === 'fill-in-conversation' && <p>{exercise.conversations[index].prompt}</p>}
                <input
                  type="text"
                  className="border p-2 w-full"
                  value={userAnswers[`${currentLesson}-${sectionIndex}-${exerciseIndex}`]?.answers[index] || ''}
                  onChange={(e) => {
                    const newAnswers = userAnswers[`${currentLesson}-${sectionIndex}-${exerciseIndex}`]?.answers || Array(exercise.answers.length).fill('');
                    newAnswers[index] = e.target.value;
                    setUserAnswers({
                      ...userAnswers,
                      [`${currentLesson}-${sectionIndex}-${exerciseIndex}`]: { answers: newAnswers },
                    });
                  }}
                />
              </div>
            ))}
            <button
              className="bg-blue-500 text-white px-4 py-2 rounded"
              onClick={() => handleAnswerSubmit(exercise, userAnswers[`${currentLesson}-${sectionIndex}-${exerciseIndex}`]?.answers || [])}
            >
              Submit
            </button>
          </div>
        );
      case 'match-sounds':
        return (
          <div>
            <p>{exercise.question}</p>
            <p>Sounds: {exercise.sounds.join(', ')}</p>
            {exercise.words.map((word, index) => (
              <div key={index} className="mb-2">
                <span>{word.word}</span>
                <input
                  type="text"
                  className="border p-2 ml-2"
                  value={userAnswers[`${currentLesson}-${sectionIndex}-${exerciseIndex}`]?.answers[index] || ''}
                  onChange={(e) => {
                    const newAnswers = userAnswers[`${currentLesson}-${sectionIndex}-${exerciseIndex}`]?.answers || Array(exercise.answers.length).fill('');
                    newAnswers[index] = e.target.value;
                    setUserAnswers({
                      ...userAnswers,
                      [`${currentLesson}-${sectionIndex}-${exerciseIndex}`]: { answers: newAnswers },
                    });
                  }}
                />
              </div>
            ))}
            <button
              className="bg-blue-500 text-white px-4 py-2 rounded"
              onClick={() => handleAnswerSubmit(exercise, userAnswers[`${currentLesson}-${sectionIndex}-${exerciseIndex}`]?.answers || [])}
            >
              Submit
            </button>
          </div>
        );
      case 'stress-words':
        return (
          <div>
            <p>{exercise.question}</p>
            {exercise.words.map((word, index) => (
              <div key={index} className="mb-2">
                <span>{word}</span>
                <input
                  type="text"
                  className="border p-2 ml-2"
                  value={userAnswers[`${currentLesson}-${sectionIndex}-${exerciseIndex}`]?.answers[index] || ''}
                  onChange={(e) => {
                    const newAnswers = userAnswers[`${currentLesson}-${sectionIndex}-${exerciseIndex}`]?.answers || Array(exercise.answers.length).fill('');
                    newAnswers[index] = e.target.value;
                    setUserAnswers({
                      ...userAnswers,
                      [`${currentLesson}-${sectionIndex}-${exerciseIndex}`]: { answers: newAnswers },
                    });
                  }}
                />
              </div>
            ))}
            <button
              className="bg-blue-500 text-white px-4 py-2 rounded"
              onClick={() => handleAnswerSubmit(exercise, userAnswers[`${currentLesson}-${sectionIndex}-${exerciseIndex}`]?.answers || [])}
            >
              Submit
            </button>
          </div>
        );
      case 'unscramble':
        return (
          <div>
            <p>{exercise.question}</p>
            {exercise.words.map((word, index) => (
              <div key={index} className="mb-2">
                <span>{word}</span>
                <input
                  type="text"
                  className="border p-2 ml-2"
                  value={userAnswers[`${currentLesson}-${sectionIndex}-${exerciseIndex}`]?.answers[index] || ''}
                  onChange={(e) => {
                    const newAnswers = userAnswers[`${currentLesson}-${sectionIndex}-${exerciseIndex}`]?.answers || Array(exercise.answers.length).fill('');
                    newAnswers[index] = e.target.value;
                    setUserAnswers({
                      ...userAnswers,
                      [`${currentLesson}-${sectionIndex}-${exerciseIndex}`]: { answers: newAnswers },
                    });
                  }}
                />
              </div>
            ))}
            <button
              className="bg-blue-500 text-white px-4 py-2 rounded"
              onClick={() => handleAnswerSubmit(exercise, userAnswers[`${currentLesson}-${sectionIndex}-${exerciseIndex}`]?.answers || [])}
            >
              Submit
            </button>
          </div>
        );
      case 'series':
        return (
          <div>
            <p>{exercise.question}</p>
            {exercise.series.map((serie, index) => (
              <div key={index} className="mb-2">
                <span>{serie}</span>
                <input
                  type="text"
                  className="border p-2 ml-2"
                  value={userAnswers[`${currentLesson}-${sectionIndex}-${exerciseIndex}`]?.answers[index] || ''}
                  onChange={(e) => {
                    const newAnswers = userAnswers[`${currentLesson}-${sectionIndex}-${exerciseIndex}`]?.answers || Array(exercise.answers.length).fill('');
                    newAnswers[index] = e.target.value;
                    setUserAnswers({
                      ...userAnswers,
                      [`${currentLesson}-${sectionIndex}-${exerciseIndex}`]: { answers: newAnswers },
                    });
                  }}
                />
              </div>
            ))}
            <button
              className="bg-blue-500 text-white px-4 py-2 rounded"
              onClick={() => handleAnswerSubmit(exercise, userAnswers[`${currentLesson}-${sectionIndex}-${exerciseIndex}`]?.answers || [])}
            >
              Submit
            </button>
          </div>
        );
      case 'spelling-dialogue':
        return (
          <div>
            <p>{exercise.question}</p>
            {exercise.dialogues.map((dialogue, index) => (
              <div key={index} className="mb-2">
                <span>{dialogue}</span>
                <input
                  type="text"
                  className="border p-2 ml-2"
                  value={userAnswers[`${currentLesson}-${sectionIndex}-${exerciseIndex}`]?.answers[index] || ''}
                  onChange={(e) => {
                    const newAnswers = userAnswers[`${currentLesson}-${sectionIndex}-${exerciseIndex}`]?.answers || Array(exercise.answers.length).fill('');
                    newAnswers[index] = e.target.value;
                    setUserAnswers({
                      ...userAnswers,
                      [`${currentLesson}-${sectionIndex}-${exerciseIndex}`]: { answers: newAnswers },
                    });
                  }}
                />
              </div>
            ))}
            <button
              className="bg-blue-500 text-white px-4 py-2 rounded"
              onClick={() => handleAnswerSubmit(exercise, userAnswers[`${currentLesson}-${sectionIndex}-${exerciseIndex}`]?.answers || [])}
            >
              Submit
            </button>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-100">
      {/* Sidebar */}
      <div className="w-1/4 bg-white p-6 shadow-md">
        <h2 className="text-2xl font-bold mb-4">Table of Contents</h2>
        {lessons.map((lesson, lessonIndex) => (
          <div key={lesson.id} className="mb-4">
            <h3
              className={`text-lg font-semibold cursor-pointer ${currentLesson === lessonIndex ? 'text-blue-500' : ''}`}
              onClick={() => {
                setCurrentLesson(lessonIndex);
                setCurrentSection(0);
                setCurrentExercise(0);
              }}
            >
              {lesson.title}
            </h3>
            <ul className="ml-4">
              {lesson.sections.map((section, sectionIndex) => (
                <li
                  key={sectionIndex}
                  className={`cursor-pointer ${currentLesson === lessonIndex && currentSection === sectionIndex ? 'text-blue-500' : ''}`}
                  onClick={() => {
                    setCurrentLesson(lessonIndex);
                    setCurrentSection(sectionIndex);
                    setCurrentExercise(0);
                  }}
                >
                  {section.title}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* Main Content */}
      <div className="w-3/4 p-6">
        {isCompleted ? (
          <div>
            <h2 className="text-2xl font-bold mb-4">Results</h2>
            <p>Your Score: {score} / {results?.total}</p>
            <ul>
              {results?.details.map((detail, index) => (
                <li key={index}>
                  {detail.lesson}: {detail.correct ? 'Correct' : `Incorrect (Correct: ${detail.correctAnswers.join(', ')})`}
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <>
            <h2 className="text-2xl font-bold mb-4">{lessons[currentLesson].title}</h2>
            <h3 className="text-xl font-semibold mb-2">{lessons[currentLesson].sections[currentSection].title}</h3>
            {renderExercise(lessons[currentLesson].sections[currentSection].exercises[currentExercise], currentSection, currentExercise)}
          </>
        )}
      </div>
    </div>
  );
};

export default Unit1;